import { AttachmentDtoKind } from '../services/api.clients.generated';

/** Framework-agnostic helpers for rendering message attachments. */

type AttachmentLike = {
  kind?: AttachmentDtoKind;
  contentType?: string;
  filename?: string;
  storageId?: string;
  linkUrl?: string;
};

type AttachmentType = 'link' | 'pdf' | 'image' | 'other';

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'heic', 'tif', 'tiff']);

/**
 * Resolves an attachment's display type. Prefers `contentType`, then falls back
 * to the filename/storageId extension so attachments stored without a content
 * type (e.g. only a "Test.pdf" name) are still classified correctly.
 */
function resolveAttachmentType(attachment: AttachmentLike): AttachmentType {
  if (attachment.kind === AttachmentDtoKind.LINK) {
    return 'link';
  }

  const contentType = attachment.contentType?.toLowerCase() ?? '';
  if (contentType === 'application/pdf') {
    return 'pdf';
  }
  if (contentType.startsWith('image/')) {
    return 'image';
  }

  const name = (attachment.filename || attachment.storageId || attachment.linkUrl || '').toLowerCase();
  const extension = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  if (extension === 'pdf') {
    return 'pdf';
  }
  if (IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }
  return 'other';
}

export function getAttachmentIcon(attachment: AttachmentLike): string {
  switch (resolveAttachmentType(attachment)) {
    case 'link':
      return 'link';
    case 'pdf':
      return 'picture_as_pdf';
    case 'image':
      return 'image';
    default:
      return 'attach_file';
  }
}

export function getAttachmentLabel(
  attachment: { filename?: string; storageId?: string; linkUrl?: string },
  fallback = ''
): string {
  return attachment.filename || attachment.storageId || attachment.linkUrl || fallback;
}

/** Human-readable size, e.g. "876 Ko" / "2.4 Mo". Empty string when unknown. */
export function formatAttachmentSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) {
    return '';
  }

  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${Math.round(kilobytes)} Ko`;
  }
  return `${(kilobytes / 1024).toFixed(1)} Mo`;
}

/** Accent color for an attachment's icon tile, by file type (matches the icon glyph). */
export function getAttachmentColor(attachment: AttachmentLike): string {
  switch (resolveAttachmentType(attachment)) {
    case 'link':
      return '#2f9ec9';
    case 'pdf':
      return '#d64545';
    case 'image':
      return '#3b6fd4';
    default:
      return '#6b7a86';
  }
}
